import React, { useCallback, useMemo, useState } from "react";
import { Layout, Avatar, Button, Dropdown, Modal } from "antd";
import { App } from "antd";
import { ApiOutlined, BankOutlined, CheckOutlined, DownOutlined, KeyOutlined, LogoutOutlined, MailOutlined, MenuOutlined, ReloadOutlined, SettingOutlined, UserOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { getRoutePageLabel, resolveNewFrontendHref } from "../../app/navigation";
import { APP_INFO } from "../../config/appConfig";
import { useAuth } from "../../hooks/useAuth";
import TimeZoneSelector from "../../components/TimeZoneSelector";
// import { getActionItemsCount } from "../../api/actionItemApi";
import EmailConfigModal from "../../features/userManagement/EmailConfig/EmailConfigModal";
import { clearAllViewRecords } from "../../utils/helpDocumentTracker";
import { InlineHelpButton } from "../common/HelpButton";
import { useSidebar } from "./SidebarContext";

const { Header } = Layout;

const AppHeader = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { collapsed, toggleSidebar } = useSidebar();
    const { user, logout, assignedOrganizations, accessibleOrganizations, activeOrganization, setActiveOrganizationId } = useAuth();
    const { message } = App.useApp();
    // const [pendingNotificationsCount, setPendingNotificationsCount] = useState(0);
    const [emailConfigModalVisible, setEmailConfigModalVisible] = useState(false);
    const displayName = getUserDisplayName(user);
    const userEmail = getUserEmail(user);
    const avatarText = displayName.slice(0, 1).toUpperCase();
    const pageTitle = useMemo(() => getRoutePageLabel(location.pathname) || APP_INFO.FULL_NAME, [location.pathname]);

    // 检查用户是否有查看通知的权限
    // 通知中心只对拥有完整flight_operation:read权限的用户开放，不包括basic权限
    // const canViewNotifications = hasPermission("flight_operation:read");

    // 获取待处理通知数量
    // useEffect(() => {
    //     // 只有有权限的用户才进行API调用
    //     if (!canViewNotifications) {
    //         setPendingNotificationsCount(0);
    //         return;
    //     }
    //
    //     const fetchCount = async () => {
    //         try {
    //             const count = await getActionItemsCount();
    //             setPendingNotificationsCount(count);
    //         } catch (error) {
    //             console.error("获取待处理通知数量失败:", error);
    //             // 如果是权限错误，停止后续轮询
    //             if (error.response?.status === 403 || error.response?.status === 401) {
    //                 console.warn("用户无权限访问通知接口，停止轮询");
    //                 return;
    //             }
    //             // 其他错误情况下，设置为0但不影响轮询
    //             setPendingNotificationsCount(0);
    //         }
    //     };
    //
    //     // 首次加载时获取
    //     fetchCount();
    //
    //     // 设置定时轮询，每60秒刷新一次（从30秒改为60秒，进一步减少请求频率）
    //     const intervalId = setInterval(fetchCount, 60000);
    //
    //     // 组件卸载时清除定时器
    //     return () => clearInterval(intervalId);
    // }, [canViewNotifications]); // 依赖权限状态

    const selectableOrganizations = useMemo(() => (accessibleOrganizations.length > 0 ? accessibleOrganizations : assignedOrganizations), [accessibleOrganizations, assignedOrganizations]);

    // 菜单点击处理
    const handleMenuClick = useCallback(({ key }) => {
        if (key === "logout") {
            logout();
            message.success("您已成功退出登录");
        } else if (key === "settings") {
            // 个人设置逻辑
            message.info("个人设置功能开发中");
        } else if (key === "emailConfig") {
            // 打开邮箱配置模态框
            setEmailConfigModalVisible(true);
        } else if (key === "changePassword") {
            window.location.href = resolveNewFrontendHref("/settings/password");
        } else if (key === "mcpInstall") {
            window.location.href = resolveNewFrontendHref("/tools/mcp");
        } else if (key === "resetHelp") {
            // 清空所有帮助文档阅读记录
            Modal.confirm({
                title: "重新查看帮助说明",
                content: "确定要清空所有帮助说明的查看记录吗？这样您就可以重新查看所有的功能说明了。",
                okText: "确定",
                cancelText: "取消",
                onOk() {
                    clearAllViewRecords();
                    message.success("已将所有帮助说明标记为未查看，您可以重新查看");
                },
            });
        }
    }, [logout, message]);

    const handleOrganizationChange = useCallback(
        (organizationId) => {
            if (!setActiveOrganizationId(Number(organizationId))) {
                message.error("切换组织失败，请刷新页面后重试");
                return;
            }
            message.success("已切换当前组织");
            navigate(0);
        },
        [message, navigate, setActiveOrganizationId],
    );

    const handleSidebarTriggerClick = useCallback(() => {
        toggleSidebar();
    }, [toggleSidebar]);

    // 用户菜单项
    const userMenuItems = useMemo(
        () => [
            {
                key: "identity",
                disabled: true,
                label: (
                    <div className="app-header-user-menu__identity">
                        <span className="app-header-user-menu__name">{displayName}</span>
                        <span className="app-header-user-menu__email">{userEmail}</span>
                        {user?.username ? <span className="app-header-user-menu__username">@{user.username}</span> : null}
                    </div>
                ),
            },
            {
                key: "identity-divider",
                type: "divider",
            },
            {
                key: "settings",
                icon: <SettingOutlined />,
                label: "个人设置",
            },
            {
                key: "emailConfig",
                icon: <MailOutlined />,
                label: "邮箱配置",
            },
            {
                key: "changePassword",
                icon: <KeyOutlined />,
                label: "修改密码",
            },
            {
                key: "mcpInstall",
                icon: <ApiOutlined />,
                label: "连接器（MCP）安装教程",
            },
            {
                key: "resetHelp",
                icon: <ReloadOutlined />,
                label: "重新查看帮助说明",
            },
            {
                key: "divider",
                type: "divider",
            },
            {
                key: "logout",
                icon: <LogoutOutlined />,
                label: "退出登录",
                danger: true,
            },
        ],
        [displayName, user?.username, userEmail],
    );

    // 使用useMemo缓存菜单配置，防止每次渲染重新创建
    const dropdownMenu = useMemo(
        () => ({
            items: userMenuItems,
            onClick: handleMenuClick,
        }),
        [handleMenuClick, userMenuItems],
    );

    const organizationMenu = useMemo(
        () => ({
            items: [
                {
                    key: "organization-label",
                    disabled: true,
                    label: <span className="app-header-menu-label">组织上下文</span>,
                },
                {
                    key: "organization-divider",
                    type: "divider",
                },
                ...selectableOrganizations.map((organization) => {
                    const active = organization.id === activeOrganization?.id;
                    return {
                        key: String(organization.id),
                        disabled: active,
                        label: (
                            <span className="app-header-organization-option">
                                <span className="app-header-organization-option__label">{getOrganizationLabel(organization)}</span>
                                {active ? <CheckOutlined className="app-header-organization-option__check" /> : null}
                            </span>
                        ),
                    };
                }),
            ],
            onClick: ({ key }) => {
                if (key === "organization-label" || key === "organization-divider") {
                    return;
                }
                handleOrganizationChange(key);
            },
        }),
        [activeOrganization?.id, handleOrganizationChange, selectableOrganizations],
    );

    const activeOrganizationLabel = activeOrganization ? getOrganizationLabel(activeOrganization) : "选择组织";

    return (
        <Header className="app-header">
            <div className="app-header__left">
                <button className="app-header__sidebar-trigger" type="button" aria-expanded={!collapsed} aria-label={collapsed ? "展开导航菜单" : "收起导航菜单"} onClick={handleSidebarTriggerClick}>
                    <MenuOutlined />
                </button>
                <div className="app-header__page" title={pageTitle}>
                    <h1 className="app-header__page-title">{pageTitle}</h1>
                </div>
            </div>

            <div className="app-header__actions">
                <InlineHelpButton
                    className="app-header__icon-action app-header__help-action"
                    contentPath="help/dashboard-navigation.md"
                    title="系统导航说明"
                    tooltip="查看系统导航说明"
                    showUnviewedIndicator={false}
                    style={{
                        width: 32,
                        height: 32,
                        padding: 0,
                        color: "var(--fm-raw-muted-foreground)",
                        border: "none",
                        boxShadow: "none",
                    }}
                />
                {selectableOrganizations.length > 0 ? (
                    <Dropdown menu={organizationMenu} placement="bottomRight" trigger={["click"]}>
                        <Button className="app-header__organization-button" aria-label="切换组织">
                            <BankOutlined className="app-header__aux-icon" />
                            <span className="app-header__organization-label">{activeOrganizationLabel}</span>
                            <DownOutlined className="app-header__aux-icon app-header__chevron" />
                        </Button>
                    </Dropdown>
                ) : (
                    <Button className="app-header__organization-button app-header__organization-button--empty" disabled>
                        <BankOutlined className="app-header__aux-icon" />
                        <span className="app-header__organization-label">无组织上下文</span>
                    </Button>
                )}
                <div className="app-header__timezone">
                    <TimeZoneSelector />
                </div>

                {/* 通知图标和徽章暂时隐藏，原实现保留方便恢复。 */}
                {/* {canViewNotifications && (
                    <>
                        <Tooltip title={`您有 ${pendingNotificationsCount} 条待处理的航班动态通知`}>
                            <Link to="/workspace/notification-center" style={{ marginRight: "16px" }}>
                                <Badge count={pendingNotificationsCount} overflowCount={300} size="small">
                                    <BellOutlined style={{ fontSize: "18px", color: "#1677ff" }} />
                                </Badge>
                            </Link>
                        </Tooltip>

                        <Divider type="vertical" style={{ margin: "0 16px", height: "20px" }} />
                    </>
                )} */}

                <Dropdown menu={dropdownMenu} placement="bottomRight" trigger={["click"]}>
                    <Button className="app-header__user-button" type="text" aria-label={`用户菜单：${displayName}`}>
                        <Avatar className="app-header__avatar" icon={avatarText ? null : <UserOutlined />}>
                            {avatarText || null}
                        </Avatar>
                        <span className="app-header__user-name">{displayName}</span>
                        <DownOutlined className="app-header__aux-icon app-header__chevron" />
                    </Button>
                </Dropdown>
            </div>

            {/* 邮箱配置模态框 */}
            <EmailConfigModal visible={emailConfigModalVisible} onClose={() => setEmailConfigModalVisible(false)} />
        </Header>
    );
};

function getUserDisplayName(user) {
    return user?.full_name || user?.fullName || user?.name || user?.username || "未登录";
}

function getUserEmail(user) {
    return user?.email || user?.email_address || "共享旧前端登录态";
}

function getOrganizationLabel(organization) {
    return organization?.name || organization?.code || `组织 ${organization?.id}`;
}

export default AppHeader;
